import { useEffect, useState } from 'react';

import type { IProspect } from '../../interfaces/prospects';
import ProspectsService from '../../services/api/prospects/prospects';
import { useUser } from '../useUser/useUser';

export interface IUseClientProspectParams {
  prospectId?: number | null;
  documentValue?: string | null;
}

export interface IUseClientProspectResult {
  prospect: IProspect | null;
  prospectId: number | null;
  loading: boolean;
  error: boolean;
  refetch: () => void;
}

export const useClientProspect = ({
  prospectId,
  documentValue
}: IUseClientProspectParams): IUseClientProspectResult => {
  const { brokerId, loading: userLoading } = useUser();
  const [prospect, setProspect] = useState<IProspect | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const hasValidProspectId = typeof prospectId === 'number' && !Number.isNaN(prospectId);
  const hasLookup = hasValidProspectId || Boolean(documentValue);

  useEffect(() => {
    if (userLoading) return;

    if (!brokerId) {
      setProspect(null);
      setLoading(false);
      return;
    }

    if (!hasLookup) {
      setProspect(null);
      setError(true);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(false);
      try {
        const prospects = await ProspectsService.getProspects(brokerId, {
          prospectId: hasValidProspectId ? (prospectId as number) : undefined,
          documentValue: hasValidProspectId ? undefined : (documentValue as string)
        });
        const match = hasValidProspectId
          ? prospects.find((item) => item.id === prospectId)
          : prospects.find((item) => item.documentValue === documentValue);
        if (!cancelled) setProspect(match ?? null);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [brokerId, prospectId, documentValue, hasValidProspectId, hasLookup, userLoading, reloadToken]);

  const refetch = () => {
    setError(false);
    setLoading(true);
    setReloadToken((token) => token + 1);
  };

  return { prospect, prospectId: prospect?.id ?? null, loading: loading || userLoading, error, refetch };
};
